declare global {
    interface Window {
        invokeDownloadFileAction?: (base64Data: string, fileName: string, mimeType: string) => void;
    }
}

// This export makes the file a module, allowing global augmentation
export {};

/**
 * Converts a Blob to base64 string
 * @param blob - The blob to convert
 * @returns Base64 encoded string (without data URL prefix)
 */
export async function convertBlobToBase64(blob: Blob): Promise<string> {
    console.log('[FILE-DOWNLOAD] Converting blob to base64, blob size:', blob.size);
    
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onloadend = () => {
            try {
                const result = reader.result as string;
                // Split to remove the "data:mime/type;base64," prefix
                const base64 = result.split(',')[1];
                console.log('[FILE-DOWNLOAD] Conversion successful, base64 length:', base64.length);
                resolve(base64);
            } catch (error) {
                console.error('[FILE-DOWNLOAD] Error splitting base64 data:', error);
                reject(error);
            }
        };
        
        reader.onerror = (error) => {
            console.error('[FILE-DOWNLOAD] FileReader error:', error);
            reject(error);
        };
        
        reader.readAsDataURL(blob);
    });

    
}

/**
 * Check if native download function is available
 * @returns True if bridge is available
 */
export function isNativeDownloadAvailable(): boolean {
	const available = typeof window.invokeDownloadFileAction === 'function';
	console.log('[FILE-DOWNLOAD] Native download available:', available);
	return available;
}

/**
 * Trigger native file download
 * @param blob - The file blob
 * @param fileName - Name of the file
 * @param mimeType - MIME type of the file
 */
export async function triggerNativeDownload(
	blob: Blob, 
	fileName: string, 
	mimeType: string
): Promise<void> {
	console.log('[FILE-DOWNLOAD] ====== triggerNativeDownload CALLED ======');
	console.log('[FILE-DOWNLOAD] fileName:', fileName);
	console.log('[FILE-DOWNLOAD] mimeType:', mimeType);
	console.log('[FILE-DOWNLOAD] blob size:', blob?.size);

	try {
		// Check if bridge is available
		if (!isNativeDownloadAvailable()) {
			throw new Error('Native download bridge not available');
		}

		// Convert blob to base64
		console.log('[FILE-DOWNLOAD] Starting blob conversion...');
		const base64Data = await convertBlobToBase64(blob);
		console.log('[FILE-DOWNLOAD] Calling invokeDownloadFileAction...');

		// Call native bridge
		window.invokeDownloadFileAction!(base64Data, fileName, mimeType);

		console.log('[FILE-DOWNLOAD] ====== Native download triggered successfully ======');
	} catch (error) {
		console.error('[FILE-DOWNLOAD] ====== ERROR ======');
		console.error('[FILE-DOWNLOAD] Error:', (error as Error).message);
		console.error('[FILE-DOWNLOAD] Stack:', (error as Error).stack);
		throw error;
	}
}

/**
 * Fallback browser download for when native bridge is not available
 * @param blob - The file blob
 * @param fileName - Name of the file
 */
export function fallbackBrowserDownload(blob: Blob, fileName: string): void {
	console.log('[FILE-DOWNLOAD] Using fallback browser download');

	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();

	setTimeout(() => {
		URL.revokeObjectURL(url);
		document.body.removeChild(a);
		console.log('[FILE-DOWNLOAD] Fallback download cleanup complete');
	}, 100);
}

/**
 * Smart download function that tries native first, falls back to browser
 * @param blob - The file blob
 * @param fileName - Name of the file
 * @param mimeType - MIME type of the file
 */
export async function downloadFile(
	blob: Blob, 
	fileName: string, 
	mimeType: string
): Promise<{ success: boolean; method: 'native' | 'browser' }> {
	console.log('[FILE-DOWNLOAD] downloadFile called:', { fileName, mimeType, size: blob.size });

	try {
		if (isNativeDownloadAvailable()) {
			await triggerNativeDownload(blob, fileName, mimeType);
			return { success: true, method: 'native' };
		} else {
			console.warn('[FILE-DOWNLOAD] Native bridge not available, using browser download');
			fallbackBrowserDownload(blob, fileName);
			return { success: true, method: 'browser' };
		}
	} catch (error) {
		console.error('[FILE-DOWNLOAD] Native download failed, falling back to browser');
		console.error('[FILE-DOWNLOAD] Error:', (error as Error).message);
		console.error('[FILE-DOWNLOAD] Stack:', (error as Error).stack);
		fallbackBrowserDownload(blob, fileName);
		return { success: true, method: 'browser' };
	}
}